import { List, ListItem, Grid, ListItemText, Typography } from "@mui/material";
import { getNumberOr, numberFormatCompact, sleep } from "../core/utils";
import { LinearProgressWithLabel } from "./LinearProgressBar";
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Api from "../api/Api";

const ViewPollCard = ({ poll, initialVotes }) => {
    const [votes, setVotes] = useState({ total: 0, counts: {}, maxVotes: 0 });
    const [isUpdatingVotes, setUpdatingVotes] = useState(true);
    const [isRootVisible, setIsRootVisible] = useState(true);
    const rootRef = useRef();
    const rootObserverOptions = useMemo(() => ({
        root: null,
        rootMargin: "0px",
        threshold: 0.1
    }), []);


    useEffect(() => {
        if (rootRef.current) {
            const rootObserver = new IntersectionObserver(event => setIsRootVisible(event[0].isIntersecting), rootObserverOptions)
            const element = rootRef.current;
            rootObserver.observe(element)
            return () => rootObserver.unobserve(element);
        }
    }, [rootRef, rootObserverOptions])

    const updateVotes = async (/** @type {{ votes: any; }} */ data) => {
        let total = 0;
        const counts = {}
        let maxVotes = 0;
        for (const optionEntry of data.votes) {
            total += optionEntry.count;
            counts[optionEntry._id] = optionEntry.count;
            if (optionEntry.count > maxVotes) {
                maxVotes = optionEntry.count;
            }
        }
        setUpdatingVotes(true);
        const stepCount = 10;
        let hasChanges = true;
        while (hasChanges) {
            hasChanges = false;
            for (const key of Object.keys(counts)) {
                const stepSize = counts[key] / stepCount
                const newValue = Math.min(counts[key], (votes.counts[key] || 0) + stepSize);
                if (newValue !== votes.counts[key]) {
                    hasChanges = true;
                }
                votes.counts[key] = newValue
            }
            setVotes({ total, counts: { ...votes.counts }, maxVotes })
            await sleep(150)
        }
        setVotes({ total, counts, maxVotes })
        setUpdatingVotes(false);
    }

    useEffect(() => {
        setVotes({ total: 0, counts: {}, maxVotes: 0 })
        updateVotes(initialVotes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialVotes])

    useEffect(() => {
        if (!isUpdatingVotes && isRootVisible) {
            const timeoutHandler = setTimeout(async () => {
                updateVotes(await Api.getVotes(poll._id))
            }, 10000);
            return () => clearTimeout(timeoutHandler)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRootVisible, isUpdatingVotes, poll])

    return <Grid item xs={12} ref={rootRef}>
        <Grid item xs={12}>
            <List sx={{ borderColor: 'gray', borderWidth: '4px', borderStyle: 'solid', borderRadius: '16px', pb: 4 }}>
                {poll.options.map((/** @type {{ option: string; _id:String; }} */ item) => {
                    const isWinner = votes.counts[item._id] === votes.maxVotes;
                    return (
                        <ListItem
                            key={item._id}
                            disablePadding
                        >
                            <Grid container>
                                <Grid item xs={12} px={2} py={1}>
                                    <ListItemText primary={`${item.option} (${getNumberOr(votes.counts[item._id] / votes.total * 100, 0).toFixed(2)}%)`} sx={{ color: 'white', ['& .MuiListItemText-primary']: { fontWeight: isWinner ? 'bold' : undefined } }} />
                                </Grid>
                                <Grid item xs={12} pr={1} pl={2}>
                                    <LinearProgressWithLabel value={getNumberOr(votes.counts[item._id] / votes.total * 100, 0)} label={`${getNumberOr(Math.ceil(votes.counts[item._id]), 0)}`} variant={isWinner ? 'winner' : undefined} />
                                </Grid>
                            </Grid>
                        </ListItem>
                    );
                })}
            </List>
        </Grid>
        <Grid item xs={12} justifyContent={'center'} display={'flex'} mt={2}>
            <Typography sx={{ color: 'white' }}>{`${numberFormatCompact(votes.total)} votes on this poll`}</Typography>
        </Grid>
    </Grid>
}

ViewPollCard.propTypes = {
    poll: PropTypes.any.isRequired,
    initialVotes: PropTypes.any.isRequired
}

export default ViewPollCard;